import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Subject, forkJoin, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { Products, ProductCatalogFilters } from '../../../core/services/products.service';
import { AttributesService } from '../../../core/services/attributes.service';
import { Product } from '../../../shared/models/Product';
import { Brand, Category, Size, Color } from '../../../shared/models/Attribute';
import { Modelo } from '../../../shared/models/modelo';

interface SortOption {
  label: string;
  value: string;
}

interface FilterChip {
  key: string;
  label: string;
}

@Component({
  selector: 'app-shop',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    ChipModule,
    ButtonModule,
    DrawerModule
  ],
  templateUrl: './shop.html',
  styleUrl: './shop.css',
})
export class Shop implements OnInit, OnDestroy {

  products: Product[] = [];
  isLoading: boolean = true;
  filtersReady: boolean = false;
  loadError: boolean = false;
  filtersLoadError: boolean = false;

  categories: Category[] = [];
  brands: Brand[] = [];
  sizes: Size[] = [];
  colors: Color[] = [];
  modelos: Modelo[] = [];
  allModelos: Modelo[] = [];

  selectedCategoryId: number | null = null;
  selectedBrandId: number | null = null;
  selectedModeloId: number | null = null;
  selectedSizeId: number | null = null;
  selectedColorHex: string | null = null;
  searchQuery: string = '';
  inStockOnly: boolean = false;
  selectedSort: string = 'newest';

  sortOptions: SortOption[] = [
    { label: 'Novedades', value: 'newest' },
    { label: 'Precio: menor a mayor', value: 'price_asc' },
    { label: 'Precio: mayor a menor', value: 'price_desc' },
    { label: 'Nombre A-Z', value: 'name_asc' },
    { label: 'Nombre Z-A', value: 'name_desc' }
  ];

  currentPage: number = 1;
  itemsPerPage: number = 6;
  mobileFiltersVisible: boolean = false;

  private searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private syncingFromUrl = false;

  get pageNumbers(): number[] {
    const pages = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  get paginatedProducts(): Product[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.products.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.products.length / this.itemsPerPage));
  }

  get activeChips(): FilterChip[] {
    const chips: FilterChip[] = [];
    if (this.searchQuery?.trim()) {
      chips.push({ key: 'q', label: `"${this.searchQuery.trim()}"` });
    }
    if (this.selectedCategoryId != null) {
      const cat = this.categories.find(c => c.id === this.selectedCategoryId);
      chips.push({ key: 'categoryId', label: cat?.name || 'Categoría' });
    }
    if (this.selectedBrandId != null) {
      const brand = this.brands.find(b => b.id === this.selectedBrandId);
      chips.push({ key: 'brandId', label: brand?.name || 'Marca' });
    }
    if (this.selectedModeloId != null) {
      const modelo = this.allModelos.find(m => m.id === this.selectedModeloId);
      chips.push({ key: 'modeloId', label: modelo?.name || 'Modelo' });
    }
    if (this.selectedSizeId != null) {
      const size = this.sizes.find(s => s.id === this.selectedSizeId);
      chips.push({ key: 'sizeId', label: `Talla ${size?.name || ''}` });
    }
    if (this.selectedColorHex) {
      const color = this.colors.find(c => c.hexCode?.toLowerCase() === this.selectedColorHex?.toLowerCase());
      chips.push({ key: 'color', label: color?.name || this.selectedColorHex });
    }
    if (this.inStockOnly) {
      chips.push({ key: 'inStock', label: 'Con stock' });
    }
    return chips;
  }

  get hasActiveFilters(): boolean {
    return this.activeChips.length > 0;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private productService: Products,
    private attributesService: AttributesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.searchInput$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.searchQuery = value;
      this.currentPage = 1;
      this.syncUrlAndReload();
    });

    this.loadFilterOptions();

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.applyParamsFromUrl(params);
      if (this.filtersReady) {
        this.loadProducts();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFilterOptions() {
    forkJoin({
      categories: this.attributesService.getActiveCategories(),
      brands: this.attributesService.getActiveBrands(),
      sizes: this.attributesService.getActiveSizes(),
      colors: this.attributesService.getActiveColors(),
      modelos: this.attributesService.getActiveModels()
    }).subscribe({
      next: ({ categories, brands, sizes, colors, modelos }) => {
        this.categories = categories || [];
        this.brands = brands || [];
        this.sizes = sizes || [];
        this.colors = colors || [];
        this.allModelos = modelos || [];
        this.refreshModelos();
        this.filtersLoadError = false;
        this.filtersReady = true;
        this.loadProducts();
        this.cdr.detectChanges();
      },
      error: () => {
        this.filtersLoadError = true;
        this.filtersReady = true;
        this.loadProducts();
        this.cdr.detectChanges();
      }
    });
  }

  refreshModelos() {
    if (this.selectedCategoryId != null) {
      this.modelos = this.allModelos.filter(m => m.categoryId === this.selectedCategoryId);
    } else {
      this.modelos = [...this.allModelos];
    }
  }

  applyParamsFromUrl(params: Params) {
    this.syncingFromUrl = true;
    this.selectedCategoryId = params['categoryId'] ? Number(params['categoryId']) : null;
    this.selectedBrandId = params['brandId'] ? Number(params['brandId']) : null;
    this.selectedModeloId = params['modeloId'] ? Number(params['modeloId']) : null;
    this.selectedSizeId = params['sizeId'] ? Number(params['sizeId']) : null;
    this.selectedColorHex = params['color'] || null;
    this.searchQuery = params['q'] || '';
    this.inStockOnly = params['inStock'] === 'true' || params['inStock'] === true;
    this.selectedSort = params['sort'] || 'newest';
    this.currentPage = params['page'] ? Math.max(1, Number(params['page'])) : 1;
    this.refreshModelos();
    this.syncingFromUrl = false;
  }

  buildFilters(): ProductCatalogFilters {
    return {
      categoryId: this.selectedCategoryId,
      brandId: this.selectedBrandId,
      modeloId: this.selectedModeloId,
      sizeId: this.selectedSizeId,
      color: this.selectedColorHex,
      q: this.searchQuery?.trim() || null,
      inStock: this.inStockOnly ? true : null,
      sort: this.selectedSort || 'newest'
    };
  }

  syncUrlAndReload() {
    if (this.syncingFromUrl) return;

    const queryParams: Params = {};
    if (this.selectedCategoryId != null) queryParams['categoryId'] = this.selectedCategoryId;
    if (this.selectedBrandId != null) queryParams['brandId'] = this.selectedBrandId;
    if (this.selectedModeloId != null) queryParams['modeloId'] = this.selectedModeloId;
    if (this.selectedSizeId != null) queryParams['sizeId'] = this.selectedSizeId;
    if (this.selectedColorHex) queryParams['color'] = this.selectedColorHex;
    if (this.searchQuery?.trim()) queryParams['q'] = this.searchQuery.trim();
    if (this.inStockOnly) queryParams['inStock'] = true;
    if (this.selectedSort && this.selectedSort !== 'newest') queryParams['sort'] = this.selectedSort;
    if (this.currentPage > 1) queryParams['page'] = this.currentPage;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  loadProducts() {
    this.isLoading = true;
    this.loadError = false;
    this.productService.getCatalogProducts(this.buildFilters()).subscribe({
      next: (data) => {
        this.products = Array.isArray(data) ? data : [];
        this.loadError = !Array.isArray(data);
        if (this.currentPage > this.totalPages) {
          this.currentPage = 1;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.products = [];
        this.loadError = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  retryLoad() {
    if (this.filtersLoadError) {
      this.loadFilterOptions();
      return;
    }
    this.loadProducts();
  }

  onSearchInput(value: string) {
    this.searchInput$.next(value ?? '');
  }

  onCategoryChange() {
    this.selectedModeloId = null;
    this.refreshModelos();
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  onBrandChange() {
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  onModeloChange() {
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  onSizeChange() {
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  onSortChange() {
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  onInStockChange() {
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  selectColor(hex: string | null) {
    this.selectedColorHex = this.selectedColorHex === hex ? null : hex;
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  removeChip(key: string) {
    switch (key) {
      case 'q':
        this.searchQuery = '';
        break;
      case 'categoryId':
        this.selectedCategoryId = null;
        this.selectedModeloId = null;
        this.refreshModelos();
        break;
      case 'brandId':
        this.selectedBrandId = null;
        break;
      case 'modeloId':
        this.selectedModeloId = null;
        break;
      case 'sizeId':
        this.selectedSizeId = null;
        break;
      case 'color':
        this.selectedColorHex = null;
        break;
      case 'inStock':
        this.inStockOnly = false;
        break;
    }
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  clearFilters() {
    this.selectedCategoryId = null;
    this.selectedBrandId = null;
    this.selectedModeloId = null;
    this.selectedSizeId = null;
    this.selectedColorHex = null;
    this.searchQuery = '';
    this.inStockOnly = false;
    this.selectedSort = 'newest';
    this.currentPage = 1;
    this.refreshModelos();
    this.syncUrlAndReload();
  }

  changePage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.syncUrlAndReload();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  verProducto(product: Product) {
    this.router.navigate(['/product', product.slug]);
  }

  openMobileFilters() {
    this.mobileFiltersVisible = true;
  }

  closeMobileFilters() {
    this.mobileFiltersVisible = false;
  }

  applyMobileFilters() {
    this.mobileFiltersVisible = false;
    this.currentPage = 1;
    this.syncUrlAndReload();
  }

  isColorSelected(hex: string): boolean {
    return !!this.selectedColorHex && this.selectedColorHex.toLowerCase() === hex?.toLowerCase();
  }

  trackByProductId(_: number, product: Product): number {
    return product.id;
  }

  hasOffer(p: Product): boolean {
    return !!p.isOffer && !!p.priceOffer && p.priceOffer > 0 && p.priceOffer < (p.price || 0);
  }
}
